import { Navigate, Outlet } from 'react-router';
import { useAuth } from '@/context/auth';
import { Skeleton } from '@/components/ui/Skeleton';
import type { Permission, Role } from '@/util/permissions';

/** Requires a session. Agents without a staff role are turned away. */
export default function ProtectedRoute() {
  const { isLoading, isAuthenticated, roles } = useAuth();

  if (isLoading) {
    return (
      <div className="space-y-4 p-8">
        <Skeleton className="h-12 w-1/3" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/sign-in" replace />;

  // This dashboard is back-office only; an agent has no staff group.
  if (roles.length === 0) return <Navigate to="/sin-acceso" replace />;

  return <Outlet />;
}

/**
 * Gates a screen on a permission or an explicit role list.
 * The server re-checks the same map via requirePermission().
 */
export function RoleRoute({
  children,
  allow,
  permission,
}: {
  children: React.ReactNode;
  allow?: Role[];
  permission?: Permission;
}) {
  const { roles, can, isLoading } = useAuth();

  if (isLoading) return null;

  if (allow && !roles.some((role) => allow.includes(role))) {
    return <Navigate to="/" replace />;
  }

  if (permission && !can(permission)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return null;
  if (isAuthenticated) return <Navigate to="/" replace />;

  return <>{children}</>;
}
